import apiSlice from 'app/apiSlice';
import { createEntityAdapter } from '@reduxjs/toolkit';
import {
  creativesParams,
  pageParams,
  searchParams,
} from 'helpers/paramHelpers';

// Create an entity adapter for users
const templatesAdapter = createEntityAdapter();

const templatesApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getTemplates: builder.query({
      query: payload => ({
        url: `templates`,
        params: {
          ...creativesParams(payload),
          ...pageParams(payload),
          ...searchParams(payload),
        },
      }),
      transformResponse: response => {
        const normalized = templatesAdapter.setAll(
          templatesAdapter.getInitialState(),
          response.data || []
        );

        const restructured = {
          ...normalized,
          entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
        };

        return {
          ...response,
          data: restructured,
        };
      },
      providesTags: ['Templates'], //tag for refetch when invalidateTags api trggered
    }),

    getTemplateById: builder.query({
      query: id => ({
        url: `templates/${id}`,
      }),
    }),

    getUserFonts: builder.query({
      query: params => ({
        url: `fonts?scope=all&grouped`,
        params: {
          ...params,
        },
      }),
      transformResponse: (response, meta, arg) => {
        // Use 'arg' (which is the same as `params`) to conditionally transform

        if (arg?.normalize === true) {
          const normalized = templatesAdapter.setAll(
            templatesAdapter.getInitialState(),
            response.data.data.private || []
          );

          const restructured = {
            ...normalized,
            entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
          };

          return {
            ...response,
            data: restructured,
          };
        }

        return response.data.data; // skip normalization
      },
      providesTags: ['UserFonts'], //tag for refetch when invalidateTags api trggered
    }),

    getTemplatePreview: builder.mutation({
      query: data => {
        if (data.template.double_sided === true) {
          delete data.template.ad_template_pages;
        }

        const formData = new FormData();

        for (let key in data) {
          if (key === 'template' || key === 'params') {
            formData.append(`${key}`, JSON.stringify(data[key]));
          } else {
            formData.append(`${key}`, data[key]);
          }
        }
        //For debugging
        // for (let key of formData.entries()) {
        //   console.log(`${key[0]}: ${key[1]}`);
        // }

        return {
          url: `fulfillments/preview`,
          method: 'POST',
          body: formData,
        };
      },
    }),

    getUserFontById: builder.query({
      query: id => ({
        url: `fonts/${id}`,
      }),
    }),

    getSizeFormats: builder.query({
      query: () => ({
        url: `size_formats`, // TODO: "path" should hit the enabled_size_formats table isntead of size_formats
      }),
      transformResponse: response => {
        return response.data;
      },
    }),

    getEnabledDoubleSideStatus: builder.query({
      query: () => ({
        url: `feature_settings`,
      }),
    }),

    getPremadeTemplates: builder.query({
      query: () => ({
        url: `template_samples`,
      }),
    }),

    getMruVariableKeys: builder.query({
      query: () => ({
        url: `ad_template_var_keys/mru?size=10`,
      }),
      transformResponse: response => {
        const mruArray = response.data.map(mru => {
          return {
            name: mru.key,
            value: `{{${mru.key}}}`,
          };
        });
        return mruArray;
      },
    }),

    createTemplate: builder.mutation({
      query: data => {
        // If data is already FormData, just use it directly
        const isFormData = data instanceof FormData;

        const formData = isFormData ? data : new FormData();

        if (!data.ad_template_vars) {
          data.ad_template_vars = [];
        }

        if (!data.ad_template_pages) {
          data.ad_template_pages = [];
        }

        for (let key in data) {
          if (
            key === 'ad_template_vars' ||
            key === 'metadata' ||
            key === 'ad_template_pages'
          ) {
            formData.append(`${key}`, JSON.stringify(data[key]));
          } else {
            formData.append(`${key}`, data[key]);
          }
        }

        //For debugging
        // for (let key of formData.entries()) {
        //   console.log(`${key[0]}: ${key[1]}`);
        // }

        return {
          url: `templates`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Templates'], //trigger refetch for the providesTags api
    }),

    createDoubleSidedTemplate: builder.mutation({
      query: payload => {
        const formData = new FormData();
        const payloadClone = { ...payload };

        if (!payload.ad_template_vars) {
          payload.ad_template_vars = [];
        }

        if (!payload.ad_template_pages) {
          payload.ad_template_pages = [];
        }

        if (
          payload.ad_template_pages[0]?.bg_img ||
          payload.ad_template_pages[1]?.bg_img
        ) {
          for (let i = 0; i < payload.ad_template_pages.length; i++) {
            if (payload.ad_template_pages[i]?.bg_img) {
              formData.append(
                payload.ad_template_pages[i].bg_img.name,
                payload.ad_template_pages[i].bg_img
              );
              payloadClone.ad_template_pages[i].bg_img =
                payloadClone.ad_template_pages[i].bg_img.name;
            }
          }
          formData.append('data', JSON.stringify(payloadClone));

          //For debugging
          // for (let key of formData.entries()) {
          //   console.log(`${key[0]}: ${key[1]}`);
          // }
          return {
            url: `templates`,
            method: 'POST',
            body: formData,
          };
        } else if (payload?.bg_img) {
          for (let key in payload) {
            if (
              key === 'ad_template_vars' ||
              key === 'metadata' ||
              key === 'ad_template_pages'
            ) {
              formData.append(`${key}`, JSON.stringify(payload[key]));
            } else {
              formData.append(`${key}`, payload[key]);
            }
          }
          return {
            url: `templates`,
            method: 'POST',
            body: formData,
          };
        } else {
          return {
            url: `templates`,
            method: 'POST',
            body: payload,
          };
        }
      },
      invalidatesTags: ['Templates'], //trigger refetch for the providesTags api
    }),

    updateDoubleSidedTemplateById: builder.mutation({
      query: ({ id, payload }) => {
        const formData = new FormData();
        const payloadClone = { ...payload };

        if (!payload.ad_template_vars) {
          payload.ad_template_vars = [];
        }

        if (!payload.ad_template_pages) {
          payload.ad_template_pages = [];
        }

        if (
          payload.ad_template_pages[0]?.bg_img ||
          payload.ad_template_pages[1]?.bg_img
        ) {
          for (let i = 0; i < payload.ad_template_pages.length; i++) {
            if (payload.ad_template_pages[i]?.bg_img) {
              formData.append(
                payload.ad_template_pages[i].bg_img.name,
                payload.ad_template_pages[i].bg_img
              );
              payloadClone.ad_template_pages[i].bg_img =
                payloadClone.ad_template_pages[i].bg_img.name;
            }
          }
          formData.append('data', JSON.stringify(payloadClone));

          //For debugging
          // for (let key of formData.entries()) {
          //   console.log(`${key[0]}: ${key[1]}`);
          // }

          return {
            url: `templates/${id}`,
            method: 'PUT',
            body: formData,
          };
        } else if (payload.bg_img) {
          for (let key in payload) {
            if (
              key === 'ad_template_vars' ||
              key === 'metadata' ||
              key === 'ad_template_pages'
            ) {
              formData.append(`${key}`, JSON.stringify(payload[key]));
            } else {
              formData.append(`${key}`, payload[key]);
            }
          }
          return {
            url: `templates/${id}`,
            method: 'PUT',
            body: formData,
          };
        } else {
          return {
            url: `templates/${id}`,
            method: 'PUT',
            body: payload,
          };
        }
      },
      invalidatesTags: ['Templates'], //trigger refetch for the providesTags api
    }),

    copyPremadeTemplate: builder.mutation({
      // Payload sample: "const payload = { id }", where id is the template id to copy.
      // Eligible templates to copy should have a "company_id" matching user's company_id
      // or have "ad_template_company_id: null"
      query: data => ({
        url: `template_copy`,
        method: 'POST',
        body: data,
      }),
    }),

    addUserFont: builder.mutation({
      query: data => ({
        url: `fonts`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UserFonts'], //trigger refetch for the providesTags api
    }),

    updateTemplateById: builder.mutation({
      query: ({ id, data }) => {
        const formData = new FormData();

        for (let key in data) {
          if (key === 'ad_template_vars' || key === 'metadata') {
            formData.append(`${key}`, JSON.stringify(data[key]));
          } else {
            formData.append(`${key}`, data[key]);
          }
        }

        //For debugging
        // for (let key of formData.entries()) {
        //   console.log(`${key[0]}: ${key[1]}`);
        // }

        return {
          url: `templates/${id}`,
          method: 'PUT',
          body: formData,
        };
      },
      invalidatesTags: ['Templates'], //trigger refetch for the providesTags api
    }),

    updateUserFont: builder.mutation({
      query: ({ id, data }) => {
        return {
          url: `fonts/${id}`,
          method: 'PUT',
          body: data,
        };
      },
      invalidatesTags: ['UserFonts'], //trigger refetch for the providesTags api
    }),

    deleteTemplateById: builder.mutation({
      query: id => ({
        url: `templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Templates'], //trigger refetch for the providesTags api
    }),

    deleteUserFont: builder.mutation({
      query: id => ({
        url: `fonts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserFonts'], //trigger refetch for the providesTags api
    }),
  }),
  overrideExisting: false, // Avoid overwriting existing endpoints
});

export const {
  useGetTemplatesQuery,
  useLazyGetTemplatesQuery,
  useLazyGetTemplateByIdQuery,
  useGetUserFontsQuery,
  useLazyGetUserFontsQuery,
  useGetSizeFormatsQuery,
  useLazyGetSizeFormatsQuery,
  useLazyGetEnabledDoubleSideStatusQuery,
  useLazyGetPremadeTemplatesQuery,
  useGetMruVariableKeysQuery,
  useGetTemplatePreviewMutation,
  useUpdateTemplateByIdMutation,
  useAddUserFontMutation,
  useCreateTemplateMutation,
  useCreateDoubleSidedTemplateMutation,
  useUpdateDoubleSidedTemplateByIdMutation,
  useDeleteTemplateByIdMutation,
  useDeleteUserFontMutation,
} = templatesApi;

export default templatesApi;
